import { BoxProps, CircularProgress, CircularProgressProps, Button as MUIButton } from '@mui/material';
import React, { ComponentPropsWithoutRef } from 'react';

import { cn } from '../../lib';
import { StyledButton } from './styled';

export interface ButtonProps extends ComponentPropsWithoutRef<typeof MUIButton> {
  isLoading?: boolean;
  isFullWidth?: boolean;
  bgColor?: string;
  borderRadius?: BoxProps['borderRadius'];
  label?: string;
  labelColor?: string;
  labelSize?: BoxProps['fontSize'];
  buttonSize?: 'large' | 'regular';
  loadingProps?: CircularProgressProps;
  borderColor?: BoxProps['borderColor'];
  padding?: BoxProps['padding'];
  wrapperSx?: BoxProps['sx'];
  height?: BoxProps['height'];
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      sx,
      style,
      height,
      children,
      className,
      loadingProps,
      borderColor,
      padding,
      wrapperSx,
      disabled,
      label,
      labelColor = 'white',
      labelSize = '18px',
      bgColor = 'transparent',
      borderRadius = '16px',
      buttonSize = 'large',
      isLoading = false,
      isFullWidth = false,
      onClick,
      ...buttonProps
    },
    ref,
  ) => {
    return (
      <StyledButton
        className={cn(className)}
        style={style}
        sx={{
          width: isFullWidth ? '-webkit-fill-available' : 'fit-content',
          ...wrapperSx,
        }}>
        <MUIButton
          ref={ref}
          disabled={disabled}
          onClick={onClick}
          sx={{
            '&.MuiButton-root': {
              borderRadius,
              padding: padding ?? buttonPadding[buttonSize],
              height: height ?? buttonHeight[buttonSize],
              backgroundColor: bgColor,
              fontSize: labelSize,
              color: labelColor,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: borderColor ?? 'transparent',
            },
            '&.Mui-disabled': {
              cursor: 'not-allowed',
              pointerEvents: 'all',
            },
            ...sx,
          }}
          {...buttonProps}>
          {isLoading ? <CircularProgress {...loadingProps} /> : (children ?? label)}
        </MUIButton>
      </StyledButton>
    );
  },
);

const buttonHeight = {
  large: 56,
  regular: 40,
};

const buttonPadding = {
  large: '16px 32px',
  regular: '8px 32px',
};

Button.displayName = 'Button';

export { Button };
